/**
 * Client Projects List
 *
 * Liste des projets/chantiers du client
 * Design System: Contexte DASHBOARD
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  Search,
  MapPin,
  Calendar,
  FileCheck,
  MessageCircle,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { useClientProjects } from '../hooks/useClientPortal';
import type { ClientProject } from '../types/client-portal.types';

type FilterStatus = 'all' | 'active' | 'completed';

export function ClientProjectsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: projects, isLoading } = useClientProjects();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    return projects.filter((project) => {
      // Filter by search
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        project.name.toLowerCase().includes(searchLower) ||
        project.reference?.toLowerCase().includes(searchLower) ||
        project.address?.toLowerCase().includes(searchLower) ||
        project.city?.toLowerCase().includes(searchLower);

      // Filter by status
      let matchesStatus = true;
      if (filterStatus === 'active') {
        matchesStatus = project.progressPercent < 100;
      } else if (filterStatus === 'completed') {
        matchesStatus = project.progressPercent >= 100;
      }

      return matchesSearch && matchesStatus;
    });
  }, [projects, search, filterStatus]);

  const getProgressColor = (percent: number) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-teal-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent >= 25) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('common.clientPortal.projects.title')}</h1>
          <p className="text-gray-500 mt-1">
            {(projects?.length || 0) > 1
              ? t('common.clientPortal.projects.projectCountPlural', { count: projects?.length || 0 })
              : t('common.clientPortal.projects.projectCount', { count: projects?.length || 0 })}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.clientPortal.projects.searchPlaceholder')}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl
              text-gray-900 placeholder-gray-400
              focus:ring-2 focus:ring-teal-500/50 focus:border-transparent
              transition-all duration-200"
          />
        </div>

        {/* Status filter */}
        <div className="flex gap-2">
          {[
            { value: 'all', label: t('common.clientPortal.projects.all') },
            { value: 'active', label: t('common.clientPortal.projects.active') },
            { value: 'completed', label: t('common.clientPortal.projects.completed') },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setFilterStatus(option.value as FilterStatus)}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                filterStatus === option.value
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Projects grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => navigate(`/portal/projects/${project.id}`)}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              getProgressColor={getProgressColor}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 border border-white/40 shadow-sm text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <FolderKanban className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {search || filterStatus !== 'all'
              ? t('common.clientPortal.projects.noProjectFound')
              : t('common.clientPortal.projects.noProject')}
          </h3>
          <p className="text-gray-500">
            {search || filterStatus !== 'all'
              ? t('common.clientPortal.projects.modifyFilters')
              : t('common.clientPortal.projects.projectsWillAppear')}
          </p>
        </div>
      )}
    </div>
  );
}

// Project Card Component
function ProjectCard({
  project,
  onClick,
  formatCurrency,
  formatDate,
  getProgressColor,
}: {
  project: ClientProject;
  onClick: () => void;
  formatCurrency: (value: number) => string;
  formatDate: (date: string | null) => string;
  getProgressColor: (percent: number) => string;
}) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm
        hover:shadow-lg hover:-translate-y-0.5 cursor-pointer
        transition-all duration-300 overflow-hidden"
    >
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
              {project.reference && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                  {project.reference}
                </span>
              )}
            </div>
            {(project.address || project.city) && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {[project.address, project.city].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
      </div>

      {/* Progress */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">{t('common.clientPortal.projects.progress')}</span>
          <span className="text-sm font-semibold text-gray-900">
            {Math.round(project.progressPercent)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${getProgressColor(project.progressPercent)} rounded-full transition-all duration-500`}
            style={{ width: `${Math.min(project.progressPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 pb-4 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5 text-gray-500">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(project.startDate)}</span>
        </div>
        {project.situationsCount > 0 && (
          <div className="flex items-center gap-1.5 text-gray-500">
            <FileCheck className="w-4 h-4" />
            <span>{project.situationsCount} {project.situationsCount > 1 ? t('common.clientPortal.projects.situationsPlural') : t('common.clientPortal.projects.situations')}</span>
          </div>
        )}
        {project.unreadMessages > 0 && (
          <div className="flex items-center gap-1.5 text-blue-600">
            <MessageCircle className="w-4 h-4" />
            <span>{project.unreadMessages} {project.unreadMessages > 1 ? t('common.clientPortal.projects.unreadPlural') : t('common.clientPortal.projects.unread')}</span>
          </div>
        )}
      </div>

      {/* Pending validations badge */}
      {project.pendingValidations > 0 && (
        <div className="px-5 pb-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-xl">
            <FileCheck className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 font-medium">
              {project.pendingValidations > 1
                ? t('common.clientPortal.projects.situationsToValidatePlural', { count: project.pendingValidations })
                : t('common.clientPortal.projects.situationsToValidate', { count: project.pendingValidations })}
            </span>
          </div>
        </div>
      )}

      {/* Budget */}
      {project.budgetTTC && (
        <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">{t('common.clientPortal.projects.invoicedBudget')}</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(project.totalInvoiced)} / {formatCurrency(project.budgetTTC)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
